/**
 * UserDetailModal — quick stats pop-up for a single seller @-tag.
 *
 * Mounted once at the app root. Listens for the `v2-user-pick`
 * CustomEvent (detail.tag) — any widget that wants to surface
 * the user pop-up just dispatches that event on `window`.
 *
 * Pulls from the `user-detail` widget endpoint.
 */

import { createSignal, createEffect, Show, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import api from '../lib/apiClient.js';
import wm from '../lib/wmStore.js';
import { toast } from '../lib/toastStore.js';
import UserDetailBody from './UserDetailBody.jsx';
import { EMOJI } from '../lib/emoji.js';
import './v2-modal.css';

export default function UserDetailModal() {
  const [tag, setTag] = createSignal(null);
  const [data, setData] = createSignal(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal(null);
  const [weekOffset, setWeekOffset] = createSignal(0);

  function open(t) {
    if (!t) return;
    setTag(t);
    setWeekOffset(0);
    setData(null);
    setError(null);
  }
  function close() { setTag(null); setData(null); setError(null); }

  createEffect(() => {
    const t = tag();
    if (!t) return;
    setLoading(true);
    api.fetchWidget('user-detail', { tag: t, weekOffset: weekOffset() })
      .then((r) => { setData(r?.payload || null); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load.'))
      .finally(() => setLoading(false));
  });

  function onPick(ev) { open(ev?.detail?.tag); }
  function onKey(ev) { if (ev.key === 'Escape') close(); }
  onMount(() => {
    window.addEventListener('v2-user-pick', onPick);
    window.addEventListener('keydown', onKey);
  });
  onCleanup(() => {
    window.removeEventListener('v2-user-pick', onPick);
    window.removeEventListener('keydown', onKey);
  });

  // Issue #92 — promote the popup into a transient session window. Same
  // shared body component, just hosted by the dashboard's window manager.
  function promoteToWindow() {
    const id = wm.addTransientWidget('user-detail', { tag: tag() || '' });
    if (id) {
      toast.info('Opened as window', { ttl: 1400 });
      close();
    }
  }

  return (
    <Show when={tag()}>
      <Portal>
        <div class="v2-modal__backdrop" onClick={close}>
          <div class="v2-modal" onClick={(e) => e.stopPropagation()}>
            <header class="v2-modal__hdr">
              <span class="v2-modal__emoji">{EMOJI.user}</span>
              <span class="v2-modal__title">{tag()}</span>
              <button
                class="v2-btn-sm v2-modal__expand"
                onClick={promoteToWindow}
                title="Open as window"
                aria-label="Open as window"
              >⤢</button>
              <button class="v2-window__btns" onClick={close} title="Close (Esc)"
                style={{ background: 'transparent', border: '1px solid var(--window-edge)', color: 'var(--t-green-dim)', 'border-radius': '3px', width: '24px', height: '24px', cursor: 'pointer' }}>✖</button>
            </header>
            <UserDetailBody
              tag={tag()}
              data={data()}
              loading={loading()}
              error={error()}
              weekOffset={weekOffset()}
              onWeekOffset={setWeekOffset}
              onRefetch={() => {
                const t = tag();
                if (!t) return;
                api.fetchWidget('user-detail', { tag: t, weekOffset: weekOffset() })
                  .then((r) => { setData(r?.payload || null); setError(null); })
                  .catch(() => {});
              }}
            />
          </div>
        </div>
      </Portal>
    </Show>
  );
}
